import DeltaE from 'delta-e';
import Gradient from "javascript-color-gradient";
import { range } from 'lodash';


function computeLabels(legend, numLabels) {
  const step = Math.abs(parseFloat(legend[0].label) - parseFloat(legend[legend.length - 1].label)) / numLabels
  return range(parseFloat(legend[0].label), parseFloat(legend[legend.length - 1].label), step)
}

function computeGradient(colorsPair) {
  const colorGradient = new Gradient();
  colorGradient.setGradient(...colorsPair);

  let gradient = colorGradient.getArray();

  return gradient
}

export function computeColorLabels(legend) {
  var colorsGroups = []
  for (var i = 0; i < legend.length - 1; i++)
    colorsGroups.push([legend[i].color, legend[i + 1].color])

  let gradient = colorsGroups.reduce((a, l) => a.concat(computeGradient(l)), [])

  const labels = computeLabels(legend, gradient.length)

  return gradient.map((g, i) => {
    return { color: g, label: labels[i].toFixed(2) }
  })
}


export function computeIndex(color, legend, basicStats = {}) {
  var color1 = { L: color[0], A: color[1], B: color[2] };

  var colorsGroups = []
  for (var i = 0; i < legend.length - 1; i++)
    colorsGroups.push([legend[i].color, legend[i + 1].color])

  let gradient = colorsGroups.reduce((a, l) => a.concat(computeGradient(l)), [])

  const labels = computeLabels(legend, gradient.length)


  const label = gradient.map((g, i) => {
    return { color: g, label: labels[i]?.toFixed(3) }
  })
    .filter((gr) => {
      if ('min' in basicStats && basicStats.min !== 'NaN' && 'max' in basicStats && basicStats.max !== 'NaN')
        return (gr.label >= basicStats.min) && (gr.label <= basicStats.max)

      return true
    })
    .reduce((a, l) => {
      var aRgbHex = l.color.replace("#", "").match(/.{1,2}/g);
      var color2 = {
        L: parseInt(aRgbHex[0], 16),
        A: parseInt(aRgbHex[1], 16),
        B: parseInt(aRgbHex[2], 16)
      };
      const distRGB = DeltaE.getDeltaE76(color1, color2)

      if (distRGB < a[0]) {
        return [distRGB, l.label]
      }
      return a

    }, [10000, 'N.D'])

  const hex = "#" + (0x1000000 + (color[0] << 16) + (color[1] << 8) + color[2]).toString(16).substr(1);

  return { value: label[1], color: hex }
}


export function computeColorFromIndex(value, legend, basicStats = {}) {
  // Generate color gradients
  const colorsGroups = [];
  for (let i = 0; i < legend.length - 1; i++) {
    colorsGroups.push([legend[i].color, legend[i + 1].color]);
  }
  const gradient = colorsGroups.reduce((a, l) => a.concat(computeGradient(l)), []);

  // Generate labels
  const labels = computeLabels(legend, gradient.length);

  // Filter labels if basicStats are provided
  const filteredLabels = labels
    .map((label, i) => ({ label, color: gradient[i] }))
    .filter((item) => {
      if ('min' in basicStats && basicStats.min !== 'NaN' && 'max' in basicStats && basicStats.max !== 'NaN') {
        return item.label >= basicStats.min && item.label <= basicStats.max;
      }
      return true;
    });

  // Find the closest label to the input value
  let closest = filteredLabels.reduce((prev, curr) => {
    const prevDiff = Math.abs(prev.label - value);
    const currDiff = Math.abs(curr.label - value);
    return currDiff < prevDiff ? curr : prev;
  }, { label: Infinity, color: '#000000' });

  // Return the color
  return closest.color;
}

export function findClosestLabel(array, x) {
  console.log(array)
  return array.reduce((best, current) => {
    return (parseFloat(current.label) >= x && (!best || parseFloat(current.label) < parseFloat(best.label)))
      ? current
      : best;
  }, undefined);
}

/**
 * Predefined color ramp presets for attribute styling
 * Presets can have either start/end for simple gradients, or stops for multi-color gradients
 */
export const COLOR_RAMP_PRESETS = {
  'red-green': { start: '#FF0000', end: '#00FF00', name: 'Red to Green' },
  'green-red': { start: '#00FF00', end: '#FF0000', name: 'Green to Red' },
  'blue-red': { start: '#0000FF', end: '#FF0000', name: 'Blue to Red' },
  'yellow-blue': { start: '#FFFF00', end: '#0000FF', name: 'Yellow to Blue' },
  'viridis': { start: '#440154', end: '#fde725', name: 'Viridis' },
  'plasma': { start: '#0d0887', end: '#f0f921', name: 'Plasma' },
  'turbo': {
    name: 'Turbo',
    stops: ['#30123b', '#4662d7', '#36aaf8', '#1ae4b6', '#72fe5e', '#c8ef34', '#faba39', '#f66b19', '#cb2a04', '#7a0403'],
    start: '#30123b',
    end: '#7a0403'
  }
}

/**
 * Generates an array of colors interpolated between start and end
 * @param {string} startColor - Start color in hex format
 * @param {string} endColor - End color in hex format
 * @param {number} count - Number of colors to generate
 * @returns {string[]} Array of hex color strings
 */
export function generateColorRamp(startColor, endColor, count) {
  if (count <= 0) return []
  if (count === 1) return [startColor]

  const colorGradient = new Gradient()
  colorGradient.setGradient(startColor, endColor)
  colorGradient.setMidpoint(count)

  return colorGradient.getArray()
}

/**
 * Generates an array of colors from a preset (supports both simple and multi-stop gradients)
 * @param {Object} preset - Color ramp preset with either {start, end} or {stops} array
 * @param {number} count - Number of colors to generate
 * @returns {string[]} Array of hex color strings
 */
export function generateColorRampFromPreset(preset, count) {
  if (count <= 0) return []
  if (count === 1) return [preset.stops ? preset.stops[0] : preset.start]

  // If preset has stops array, use multi-stop gradient
  if (preset.stops && preset.stops.length > 1) {
    const colorGradient = new Gradient()
    colorGradient.setGradient(...preset.stops)
    colorGradient.setMidpoint(count)
    return colorGradient.getArray()
  }

  // Fallback to simple start/end gradient
  return generateColorRamp(preset.start, preset.end, count)
}
